import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiDownload, FiSliders } from "react-icons/fi";
import { HiCheck, HiChevronDown } from "react-icons/hi";
import Tippy from "@tippyjs/react";
import { Tag } from "../../../components/ui";
import { RECORD_CATEGORIES } from "../../../constants";

const inputClass =
  "block h-10 w-full rounded-lg border border-app-primary bg-app-input px-3 text-sm text-app-primary placeholder:text-gray-400 transition-colors focus:border-[#2470B7] focus:outline-none focus:ring-2 focus:ring-[#2470B7]/20 dark:placeholder:text-slate-500";

const labelClass = "mb-1.5 block text-xs font-medium text-app-secondary";

const tagColor = {
  bg: "bg-[#2470B7]/10",
  text: "text-[#2470B7] !text-xs !font-medium !shadow-none",
  hover: "hover:text-[#1d5d99]",
};

const UserFilters = ({
  draftFilters,
  linkedinInput,
  companyNameInput,
  locationInput,
  jobTitleInput,
  industryInput,
  emailInput,
  setEmailInput,

  setLinkedinInput,
  setCompanyNameInput,
  setLocationInput,
  setJobTitleInput,
  setIndustryInput,

  headAccounts,
  fteDropdownRef,
  fteDropdownOpen,
  setFteDropdownOpen,
  toggleFTE,
  appliedFilters,

  onEnterAddTag,
  removeTag,
  cancelFilters,
  handleExportClick,
  isDownloading,

  category,
  categoryCounts = {},
  setCategory,
}) => {
  const fields = [
    {
      label: "LinkedIn Profile",
      placeholder: "linkedin.com/in/…",
      value: linkedinInput,
      setter: setLinkedinInput,
      list: draftFilters.linkedin,
      key: "linkedin",
    },
    {
      label: "Company Name",
      placeholder: "e.g. Acme Corp",
      value: companyNameInput,
      setter: setCompanyNameInput,
      list: draftFilters.companyName,
      key: "companyName",
    },
    {
      label: "Target Geography",
      placeholder: "e.g. United States, Dubai",
      value: locationInput,
      setter: setLocationInput,
      list: draftFilters.location,
      key: "location",
    },
    {
      label: "Job Title / Seniority",
      placeholder: "e.g. VP Sales, Director",
      value: jobTitleInput,
      setter: setJobTitleInput,
      list: draftFilters.jobTitle,
      key: "jobTitle",
    },
    {
      label: "Industry Vertical",
      placeholder: "e.g. Healthcare, SaaS",
      value: industryInput,
      setter: setIndustryInput,
      list: draftFilters.industry,
      key: "industry",
    },
    {
      label: "Email Address",
      placeholder: "name@company.com",
      value: emailInput,
      setter: setEmailInput,
      list: draftFilters.email,
      key: "email",
    },
  ];

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const activeFilterCount =
    appliedFilters.headAccount.length +
    appliedFilters.linkedin.length +
    appliedFilters.companyName.length +
    appliedFilters.location.length +
    appliedFilters.jobTitle.length +
    appliedFilters.industry.length +
    appliedFilters.email.length;
  const hasAppliedFilters = activeFilterCount > 0;

  const hasPendingInput = fields.some((f) => f.value.trim());

  // Adds a typed keyword as a filter tag; returns false if it was rejected
  const commitField = (item) => {
    const value = item.value.trim();
    if (!value) return true;
    if (item.key === "email" && !isValidEmail(value)) return false;
    onEnterAddTag(value, item.key);
    item.setter("");
    return true;
  };

  // Apply commits every keyword still sitting in an input
  const applyPendingInputs = () => {
    const rejected = fields.filter((item) => !commitField(item));
    if (rejected.length) alert("Please enter a valid email address");
  };

  const selectedSizes = headAccounts.filter((acc) =>
    appliedFilters.headAccount.includes(acc.id),
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        fteDropdownRef.current &&
        !fteDropdownRef.current.contains(event.target)
      ) {
        setFteDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [fteDropdownRef, setFteDropdownOpen]);

  const categoryButton = (value, label) => {
    const selected = category === value;
    const count = categoryCounts[value];

    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={() => setCategory(value)}
        className={`flex items-center justify-center gap-2 rounded-xl text-sm font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5969C8]/40 ${
          selected
            ? "bg-white text-[#5969C8] shadow-[0_1px_3px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/70 dark:bg-slate-700 dark:text-indigo-200 dark:ring-slate-600"
            : "text-[#8090A3] hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-200"
        }`}
      >
        {selected && (
          <span
            aria-hidden="true"
            className="h-[7px] w-[7px] rounded-full bg-[#5969C8] dark:bg-indigo-300"
          />
        )}
        <span>
          {label}
          {count !== undefined && ` (${count.toLocaleString()})`}
        </span>
      </button>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* HEADER */}
      <div className="flex items-center justify-between border-b border-app-primary px-4 py-3">
        <div className="flex items-center gap-2">
          <FiSliders className="h-4 w-4 text-app-muted" />
          <h2 className="text-sm font-semibold text-app-heading">Filters</h2>
          {hasAppliedFilters && (
            <span className="rounded-full bg-[#2470B7] px-1.5 text-[11px] font-semibold leading-5 text-white">
              {activeFilterCount}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={cancelFilters}
          disabled={!hasAppliedFilters}
          className="rounded text-xs font-medium text-app-muted transition-colors hover:text-app-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reset
        </button>
      </div>

      {/* BODY */}
      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-2">
        {/* CATEGORY TOGGLE (Warm / Cold) */}
        <div>
          <div
            role="radiogroup"
            aria-label="Record type"
            className="grid h-[46px] w-full max-w-[340px] grid-cols-2 gap-1 rounded-2xl bg-[#F1F5F9] p-1 dark:bg-slate-800"
          >
            {categoryButton(RECORD_CATEGORIES.WARM, "Warm")}
            {categoryButton(RECORD_CATEGORIES.COLD, "Cold")}
          </div>
        </div>

        {/* COMPANY SIZE (FTE) */}
        <div ref={fteDropdownRef} className="relative">
          <span id="company-size-label" className={labelClass}>
            Company Size
          </span>
          <button
            type="button"
            onClick={() => setFteDropdownOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={fteDropdownOpen}
            aria-labelledby="company-size-label"
            className={`${inputClass} flex items-center justify-between text-left`}
          >
            <span
              className={selectedSizes.length ? "" : "text-gray-400"}
            >
              {selectedSizes.length
                ? `${selectedSizes.length} selected`
                : "Any size"}
            </span>
            <HiChevronDown
              className={`h-4 w-4 text-app-muted transition-transform ${
                fteDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <AnimatePresence>
            {fteDropdownOpen && (
              <motion.ul
                role="listbox"
                aria-multiselectable="true"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12 }}
                className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-app-primary bg-app-card py-1 shadow-lg"
              >
                {headAccounts.map((acc) => {
                  const selected = appliedFilters.headAccount.includes(acc.id);
                  return (
                    <li key={acc.id} role="option" aria-selected={selected}>
                      <button
                        type="button"
                        onClick={() => toggleFTE(acc.id)}
                        className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-app-primary hover:bg-app-hover"
                      >
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                            selected
                              ? "border-[#2470B7] bg-[#2470B7] text-white"
                              : "border-gray-300 dark:border-slate-500"
                          }`}
                        >
                          {selected && <HiCheck className="h-3 w-3" />}
                        </span>
                        {acc.headAccount}
                      </button>
                    </li>
                  );
                })}
              </motion.ul>
            )}
          </AnimatePresence>

          {selectedSizes.length > 0 && (
            <div className="mt-2 flex flex-wrap">
              <AnimatePresence>
                {selectedSizes.map((acc) => (
                  <Tag
                    key={acc.id}
                    label={acc.headAccount}
                    color={tagColor}
                    onRemove={() => toggleFTE(acc.id)}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* KEYWORD FIELDS + TAGS */}
        {fields.map((item) => (
          <div key={item.key}>
            <label htmlFor={`filter-${item.key}`} className={labelClass}>
              {item.label}
            </label>
            <input
              id={`filter-${item.key}`}
              type={item.key === "email" ? "email" : "text"}
              placeholder={item.placeholder}
              value={item.value}
              className={inputClass}
              onChange={(e) => item.setter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                if (!commitField(item)) {
                  alert("Please enter a valid email address");
                }
              }}
            />

            {item.list.length > 0 && (
              <div className="mt-2 flex flex-wrap">
                <AnimatePresence>
                  {item.list.map((t) => (
                    <Tag
                      key={t}
                      label={t}
                      color={tagColor}
                      onRemove={() => removeTag(item.key, t)}
                    />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* FOOTER ACTIONS */}
      <div className="flex gap-2 border-t border-app-primary px-4 py-2">
        <button
          type="button"
          onClick={applyPendingInputs}
          disabled={!hasPendingInput}
          className="inline-flex h-10 flex-1 items-center justify-center rounded-lg bg-[#2470B7] px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#1d5d99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2470B7]/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Apply Filters
        </button>

        <Tippy
          content={
            hasAppliedFilters
              ? "Export filtered data"
              : "Apply filters to export records"
          }
          placement="top"
          animation="shift-away"
          delay={100}
        >
          <span>
            <button
              type="button"
              onClick={handleExportClick}
              disabled={!hasAppliedFilters || isDownloading}
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-app-primary bg-app-card px-3 text-sm font-medium text-app-secondary transition-colors hover:border-[#2470B7]/40 hover:text-[#2470B7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2470B7]/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiDownload className="h-4 w-4" />
              {isDownloading ? "Exporting…" : "Export"}
            </button>
          </span>
        </Tippy>
      </div>
    </div>
  );
};

export default UserFilters;
